import React, {
  useState,
  createContext,
  useEffect,
  useRef,
  useContext,
} from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { HttpStatusCode } from "axios";

import CommonActionPopupModal from "../components/CommonActionPopupModal";
import SessionTimeoutModal from "../components/SessionTimeoutModal";

import { ShowToastModal } from "../redux/uiReducer";
import { SetLoggedInUserData } from "../redux/authReducer";

import { loginService } from "../WebApiServices/login.service";

import {
  AddLocalStorageData,
  GetLocalStorageData,
  RemoveLocalStorageData,
} from "../Helpers/Utilities";
import { helperText } from "../Helpers/helperText";
import { COMMON_ACTION_POPUP_TITLE, TOAST_TYPE } from "../Helpers/Enums";

const UIContext = createContext();

const UIContextProvider = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const [userDetailApiCalled, setUserDetailApiCalled] = useState(false);
  const { apiErrorMessage } = helperText.global;

  /* <summary>
  date: 13-07-2023
  Name: GM
  description: Runs the effect on route change
  <summary>*/
  useEffect(() => {
    if (!userDetailApiCalled) {
      setUserDetailApiCalled(true);
      GetUserDetails();
    }
    return () => {};
  }, [location]);

  /* <summary>
  date: 13-07-2023
  Name: GM
  description: Get user details
  <summary>*/
  async function GetUserDetails() {
    try {
      const existingUserDetails = GetLocalStorageData("eUser");
      if (!existingUserDetails) {
        setUserDetailApiCalled(false);
        return;
      }
      const { userGUID } = existingUserDetails;

      if (!userGUID) {
        setUserDetailApiCalled(false);
        return;
      }

      const response = await loginService.GetUserDetails(userGUID);

      const { status, data } = response;

      setUserDetailApiCalled(false);

      if (
        status !== HttpStatusCode.Ok &&
        status !== HttpStatusCode.Unauthorized
      ) {
        RemoveLocalStorageData("eUser");
        throw response.error;
      }

      if (status === HttpStatusCode.Ok) {
        AddLocalStorageData("eUser", data);
        dispatch(SetLoggedInUserData(data));
      }
    } catch (error) {
      dispatch(
        ShowToastModal({
          toastMessage: apiErrorMessage,
          toastType: TOAST_TYPE.Error,
        })
      );
    }
  }

  /* <summary>
  date: 26-06-2023
  Name: VK
  description: State for the confirmModal
  <summary>*/
  const [confirmModalProp, setConfirmModalProp] = useState({
    isOpen: false,
    title: "Alert",
    description: "Are you sure?",
    confirmButtonLabel: "Yes",
    disableCancelBtn: false,
  });

  //ref to resolve the confirm action
  const confirmResolveFunction = useRef();

  /* <summary>
  date: 26-06-2023
  Name: VK
  description: Method to show confirm modal and resolve the action
  <summary>*/
  const ShowConfirmModal = (
    title,
    description,
    confirmButtonLabel,
    disableCancelBtn
  ) => {
    return new Promise((resolve) => {
      setConfirmModalProp({
        isOpen: true,
        title,
        description,
        confirmButtonLabel,
        disableCancelBtn,
      });
      confirmResolveFunction.current = (choice) => {
        resolve(choice);
        setConfirmModalProp((prevState) => ({ ...prevState, isOpen: false }));
      };
    });
  };

  const [sessionTimeout, setSessionTimeout] = useState({
    show: false,
    title: "",
    message: "",
  });

  const sessionTimeoutRef = useRef();

  const OnConfirmModalCancelClicked = () => {
    confirmResolveFunction.current(false);
  };

  const OnConfirmModalConfirmClicked = () => {
    confirmResolveFunction.current(true);
  };

  async function confirmUnsavedChanges() {
    return await ShowConfirmModal(
      COMMON_ACTION_POPUP_TITLE.UnsavedChanges,
      "OK"
    );
  }

  const ToggleSessionTimeout = (title, message) => {
    return new Promise((resolve) => {
      setSessionTimeout({
        show: true,
        title,
        message,
      });

      sessionTimeoutRef.current = (choice) => {
        resolve(choice);
        setSessionTimeout((cur) => ({
          ...cur,
          show: false,
          title: "",
          message: "",
        }));
      };
    });
  };

  const OnSessionTimeoutCancel = () => sessionTimeoutRef.current(false);

  return (
    <UIContext.Provider
      value={{
        ShowConfirmModal,
        confirmUnsavedChanges,
        GetUserDetails,
        ToggleSessionTimeout,
      }}
    >
      {confirmModalProp.isOpen && (
        <CommonActionPopupModal
          title={confirmModalProp.title}
          description={confirmModalProp.description}
          confirmButtonLabel={confirmModalProp.confirmButtonLabel}
          onCancel={OnConfirmModalCancelClicked}
          OnConfirmed={OnConfirmModalConfirmClicked}
          disableCancelBtn={confirmModalProp.disableCancelBtn}
        />
      )}

      {sessionTimeout.show && (
        <SessionTimeoutModal
          title={sessionTimeout.title ?? ""}
          message={sessionTimeout.message ?? ""}
          onClose={OnSessionTimeoutCancel}
        />
      )}

      {children}
    </UIContext.Provider>
  );
};

export const useUIContext = () => {
  const context = useContext(UIContext);

  if (context === undefined) {
    throw new Error("UIContext was used outside the UIContextProvider");
  }

  return context;
};

export default UIContextProvider;
